import { useMemo } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { fontFamily, space } from "@passfirst/ui";

import { MicrophoneIcon } from "../icons";
import { type Colors, useColors } from "../theme";

interface Props {
  listening: boolean;
  onTogglePause: () => void;
  onEnd: () => void;
}

// Voice screen bottom bar: two round 64px buttons — white mic (left)
// and brick X (right). Listening shows a pause glyph (‖); paused shows
// the mic. End button has no label per spec — just the X icon.
export function VoiceBottomBar({
  listening,
  onTogglePause,
  onEnd,
}: Props): JSX.Element {
  const colors = useColors();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  return (
    <View
      style={[styles.bar, { paddingBottom: Math.max(insets.bottom, space[6]) }]}
    >
      <Pressable
        onPress={onTogglePause}
        accessibilityRole="button"
        accessibilityLabel={
          listening ? "Pause listening" : "Resume listening"
        }
        style={styles.micBtn}
      >
        {listening ? (
          <Text style={styles.pauseGlyph}>‖</Text>
        ) : (
          <MicrophoneIcon size={26} color={colors.brand} strokeWidth={1.8} />
        )}
      </Pressable>
      <Pressable
        onPress={onEnd}
        accessibilityRole="button"
        accessibilityLabel="End session"
        style={styles.endBtn}
      >
        <Text style={styles.endGlyph}>×</Text>
      </Pressable>
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
    bar: {
      flexDirection: "row",
      alignItems: "center",
      justifyContent: "center",
      gap: 16,
      paddingHorizontal: 22,
      paddingTop: space[6],
    },
    micBtn: {
      width: 64,
      height: 64,
      borderRadius: 32,
      backgroundColor: "#FFFFFF",
      alignItems: "center",
      justifyContent: "center",
    },
    pauseGlyph: {
      fontSize: 28,
      color: colors.brand,
      fontFamily: fontFamily.bold,
      lineHeight: 30,
    },
    endBtn: {
      width: 64,
      height: 64,
      borderRadius: 32,
      backgroundColor: colors.error,
      alignItems: "center",
      justifyContent: "center",
    },
    endGlyph: {
      fontSize: 32,
      color: "#FFFFFF",
      fontFamily: fontFamily.regular,
      lineHeight: 34,
    },
  });
}
